import { Controller, useForm } from "react-hook-form";
import React, { useContext, useState } from "react";
import {
  DESTROY_ACCOUNT,
  type AccountUuidVariables,
  type DestroyAccountData,
} from "../CustomerProvisioningApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import type { FetchResult } from "@apollo/client";
import {
  TextButton,
  TextInput,
  useMutationWrapper,
  AlertIndicator,
  alertTypes,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

interface DeleteAccountModalProps {
  accountUuid: string;
  accountName: string;
  organizationName: string;
}

interface DeleteAccountFormValues {
  deleteText: string;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  accountUuid,
  accountName,
  organizationName,
}) => {
  const { closeModal } = useContext(ModalContext);

  const methods = useForm<DeleteAccountFormValues>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = methods;
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const [destroyAccount] = useMutationWrapper<DestroyAccountData>(
    DESTROY_ACCOUNT,
    {
      refetchQueries: ["cnc_organizations"],
    }
  );

  const onSubmit = () => {
    setIsSubmittingForm(true);

    destroyAccount({
      variables: { account_uuid: accountUuid } as AccountUuidVariables,
    })
      .then((res: FetchResult<DestroyAccountData>) => {
        if (!!res?.data?.cnc_destroy_account?.ok) {
          closeModal();
          showToast({ message: res?.data?.cnc_destroy_account?.msg });
        } else {
          setIsSubmittingForm(false);
          showErrorDialog({ message: res.data?.cnc_destroy_account?.msg });
        }
      })
      .catch((err: Error) => {
        setIsSubmittingForm(false);
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <div>
      <AlertIndicator
        type={alertTypes.WARNING}
        wrapperClassName={"connection-alert"}
        title={`This action cannot be undone`}
        content={
          <div>
            <div>
              This will permanently delete account{" "}
              <strong>{accountName}</strong> (of{" "}
              <strong>{organizationName}</strong>).
            </div>
            <div style={{ fontWeight: 600 }}>
              All data associated with this account and tenant will be
              permanently destroyed.
            </div>
          </div>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={"deleteText"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"delete-text"}
                labelTop
                onBlur={onBlur}
                name={"deleteText"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                }}
                label={`To confirm PERMANENT DELETION of the account, please type the account name below:`}
                required
                error={errors.deleteText ? errors.deleteText : null}
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: (value) => {
              return value === accountName || "Account name is incorrect";
            },
          }}
        />
        <ModalButtonsContainer>
          <TextButton
            type="submit"
            label={"Delete Account"}
            isSecondary
            isDanger
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isSubmittingForm}
            disabled={!isValid || !isDirty || isSubmittingForm}
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default DeleteAccountModal;
